import { ackReleaseNotes, getUnseenReleaseNotes } from '../api.js';
import { getLanguage, getReleaseNotesCopy } from '../i18n.js';
import { getCurrentUser } from '../auth.js';
import { prepareReleaseNotes } from '../utils/releaseNotes.js';

const html = String.raw;

let overlayInstance = null;
let isFetching = false;
let lastUserEmail = null;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString(language || 'de', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return date.toISOString().split('T')[0];
  }
};

const buildReleaseMarkup = (releases, language, copy) =>
  releases
    .map((release) => {
      const title = escapeHtml(release.title || copy.fallbackTitle || '');
      const dateLabel = formatDate(release.createdAt, language);
      const itemsMarkup = (release.items || [])
        .map((item) => `<li>${escapeHtml(item)}</li>`)
        .join('');
      return html`
        <article class="release-note">
          <header class="release-note-header">
            <strong>${title}</strong>
            ${dateLabel ? `<span class="muted tiny">${dateLabel}</span>` : ''}
          </header>
          ${itemsMarkup ? `<ul>${itemsMarkup}</ul>` : ''}
        </article>
      `;
    })
    .join('');

const removeOverlay = () => {
  if (overlayInstance) {
    overlayInstance.remove();
    overlayInstance = null;
  }
};

const showOverlay = async ({ releases, latestReleaseId, copy, language }) => {
  if (!releases?.length) return;
  removeOverlay();

  const overlay = document.createElement('div');
  overlay.className = 'release-notes-overlay';
  overlay.innerHTML = html`
    <div class="release-notes-modal" role="dialog" aria-modal="true" aria-label="${copy.title}">
      <header>
        <h2>${copy.title}</h2>
        ${copy.subtitle ? `<p class="muted">${copy.subtitle}</p>` : ''}
      </header>
      <div class="release-notes-content">
        ${buildReleaseMarkup(releases, language, copy)}
      </div>
      <div class="release-notes-actions">
        <button type="button" class="button-primary" id="release-notes-ack">${copy.confirmButton}</button>
        <p class="muted small" id="release-notes-status"></p>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  overlayInstance = overlay;

  const ackButton = overlay.querySelector('#release-notes-ack');
  const statusEl = overlay.querySelector('#release-notes-status');

  if (ackButton) {
    ackButton.addEventListener('click', async () => {
      ackButton.disabled = true;
      if (statusEl) statusEl.textContent = copy.ackInProgress;
      try {
        await ackReleaseNotes(latestReleaseId);
        lastUserEmail = getCurrentUser()?.email ?? null;
        removeOverlay();
      } catch (error) {
        console.error('Failed to acknowledge release notes', error);
        if (statusEl) {
          statusEl.textContent = error instanceof Error ? error.message : copy.ackError;
        }
        ackButton.disabled = false;
      }
    });
  }
};

export const checkReleaseNotesOverlay = async () => {
  const user = getCurrentUser();
  if (!user) {
    lastUserEmail = null;
    removeOverlay();
    return;
  }
  if (isFetching) return;
  if (lastUserEmail && lastUserEmail === user.email && !overlayInstance) {
    return;
  }
  isFetching = true;
  try {
    const language = getLanguage();
    const copy = getReleaseNotesCopy();
    const data = await getUnseenReleaseNotes(language);
    const formatted = prepareReleaseNotes(data?.releases ?? [], language);
    if (formatted.length) {
      await showOverlay({
        releases: formatted,
        latestReleaseId: data.latestReleaseId ?? formatted[0]?.releaseId ?? null,
        copy,
        language,
      });
    } else if (data?.releases?.length && data.latestReleaseId) {
      try {
        await ackReleaseNotes(data.latestReleaseId);
        lastUserEmail = user.email;
        removeOverlay();
      } catch (error) {
        console.error('Failed to acknowledge empty release notes', error);
      }
    } else {
      lastUserEmail = user.email;
    }
  } catch (error) {
    console.error('Release notes overlay failed', error);
  } finally {
    isFetching = false;
  }
};
